import React, { useState } from 'react';
import { Link2, Copy, RefreshCw, Ban } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox, Input } from '../ui/form';
import { useGenerateRegistrationLink, useClearRegistrationLink, useUpdateDivision } from '../../hooks/use-divisions';
import { Division } from '../../types';

interface RegistrationLinkPanelProps {
  division: Division;
}

export function RegistrationLinkPanel({ division }: RegistrationLinkPanelProps) {
  const [copied, setCopied] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const generateLink = useGenerateRegistrationLink();
  const clearLink = useClearRegistrationLink();
  const updateDivision = useUpdateDivision();

  const slug = division.publicRegistrationSlug;
  const registrationUrl = slug ? `${window.location.origin}/register/${slug}` : null;
  const generateError = (generateLink.error as any)?.apiError?.message || (generateLink.error as any)?.message;

  const handleCopy = () => {
    if (!registrationUrl) return;
    navigator.clipboard.writeText(registrationUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerate = () => {
    generateLink.mutate(
      { divisionId: division.id, slug: customSlug.trim() || undefined },
      { onSuccess: () => setCustomSlug('') }
    );
  };

  const handleDisable = () => {
    if (window.confirm('Disable this division\'s public registration link? The current link will stop working immediately.')) {
      clearLink.mutate(division.id);
    }
  };

  const handleToggleListed = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateDivision.mutate({
      divisionId: division.id,
      request: {
        name: division.name,
        code: division.code,
        description: division.description,
        active: division.active,
        settings: division.settings,
        includeInPublicRegistrationList: e.target.checked,
      },
    });
  };

  return (
    <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="h-5 w-5 text-secondary-400" />
        <h5 className="text-sm font-semibold text-secondary-900 dark:text-white">
          Public Registration Link
        </h5>
      </div>
      <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
        A dedicated, shareable link that lets new users self-register directly into this division,
        without choosing from a list of every division or needing an API key.
      </p>

      <div className="mb-3">
        <Input
          label="Custom link (optional)"
          value={customSlug}
          onChange={(e) => setCustomSlug(e.target.value)}
          placeholder="acme-division"
          helperText='Lowercase letters, numbers, and hyphens only. Leave blank for a random link.'
          error={generateError}
        />
      </div>

      {registrationUrl ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-secondary-50 dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-800 dark:text-secondary-200">
              {registrationUrl}
            </code>
            <Button variant="outline" onClick={handleCopy} type="button">
              <Copy className="h-4 w-4 mr-1.5" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleGenerate}
              disabled={generateLink.isPending}
              type="button"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              {generateLink.isPending ? 'Regenerating…' : customSlug.trim() ? 'Set Custom Link' : 'Regenerate'}
            </Button>
            <Button
              variant="outline"
              onClick={handleDisable}
              disabled={clearLink.isPending}
              type="button"
            >
              <Ban className="h-4 w-4 mr-1.5" />
              {clearLink.isPending ? 'Disabling…' : 'Disable'}
            </Button>
          </div>
          <Checkbox
            label="List on the public /register page"
            checked={division.includeInPublicRegistrationList ?? false}
            onChange={handleToggleListed}
            disabled={updateDivision.isPending}
          />
        </div>
      ) : (
        <Button onClick={handleGenerate} disabled={generateLink.isPending} type="button">
          <Link2 className="h-4 w-4 mr-1.5" />
          {generateLink.isPending ? 'Generating…' : 'Generate Registration Link'}
        </Button>
      )}
    </div>
  );
}
